'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Callout } from '@galapagos/design-system/web';
import { PageTitle } from '../../components/PageTitle';
import { PrivateFields } from '../../components/PrivateFields';
import { SignupShell } from '../../components/SignupShell';
import { ensureSignupStepStyle } from '../../components/signup-step-style';
import { useStepHeading } from '../../hooks/useStepHeading';
import { useSignupLegalName, useSignupSignInContact } from '../../profile/signup-identity';
import {
  buildBookCallFrameUrl,
  isHealthieBookingCompleted,
  readBookCallCalendarUrl,
  type BookCallPrefill,
} from '../../services/book-call-calendar';
import { ensureSignupOffPathStyle } from './off-path-style';

/** Production's embed height; Healthie's calendar scrolls inside it. */
export const BOOK_CALL_FRAME_HEIGHT = 650;

/**
 * The Member Support calendar production embeds, pre-filled with what the
 * flow already knows. It sits inside the private wrapper, so session replay
 * and click tracking skip it; its URL carries the member's details to
 * Healthie and nowhere else.
 */
function BookACallCalendar({ calendar, onBooked }: { calendar: URL; onBooked: () => void }) {
  const { t } = useTranslation('signup');
  const legalName = useSignupLegalName();
  const contact = useSignupSignInContact();
  const frameRef = useRef<HTMLIFrameElement>(null);

  const src = useMemo(() => {
    const prefill: BookCallPrefill = {
      firstName: legalName?.firstName,
      lastName: legalName?.lastName,
      email: contact?.channel === 'email' ? contact.destination : undefined,
      phone: contact?.channel === 'sms' ? contact.destination : undefined,
    };
    return buildBookCallFrameUrl(calendar, prefill);
  }, [calendar, legalName, contact]);

  useEffect(() => {
    // Only the embedded frame itself, on the configured origin, may report a
    // booking; nothing from the message is kept.
    const onMessage = (event: MessageEvent) => {
      const frame = { origin: calendar.origin, window: frameRef.current?.contentWindow ?? null };
      if (isHealthieBookingCompleted(event, frame)) onBooked();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [calendar, onBooked]);

  return (
    <PrivateFields>
      <div className="signup-off-path__frame" data-testid="signup-book-call-frame">
        <iframe
          ref={frameRef}
          className="signup-off-path__frame-embed"
          src={src}
          title={t('bookCall.calendarLabel')}
          height={BOOK_CALL_FRAME_HEIGHT}
        />
      </div>
    </PrivateFields>
  );
}

/** Production's "call is booked" page, shown once Healthie reports a booking. */
function BookACallBooked() {
  const { t } = useTranslation('signup');
  const title = t('bookCall.bookedTitle');
  const headingRef = useStepHeading(title);
  return (
    <div className="signup-step" data-testid="signup-book-call-booked">
      <PageTitle title={title} headingRef={headingRef} />
      <p className="signup-off-path__body">{t('bookCall.bookedBody')}</p>
    </div>
  );
}

// Build-time config, so it is read once for the bundle.
const CALENDAR = readBookCallCalendarUrl();

/**
 * Production's care-coordinator page (Figma 1:497): heading, what the call is
 * for, an email fallback, and the shared Member Support booking calendar. When
 * no calendar link is configured, the region says online booking isn't
 * available and the email fallback stays. Like production's page, nothing here
 * leads back into the flow.
 */
function BookACall({ calendar }: { calendar: URL | null }) {
  const { t } = useTranslation('signup');
  const { t: tCommon } = useTranslation('common');
  const [booked, setBooked] = useState(false);
  const onBooked = useCallback(() => setBooked(true), []);
  const email = tCommon('siteFooter.emailAddress');
  const title = t('bookCall.title');
  const headingRef = useStepHeading(title);
  ensureSignupStepStyle();
  ensureSignupOffPathStyle();
  if (booked) return <BookACallBooked />;
  return (
    <div className="signup-step">
      <PageTitle title={title} headingRef={headingRef} />
      <Callout tone="info" title={t('bookCall.expectTitle')} body={t('bookCall.expectBody')} />
      <p className="signup-off-path__body" data-testid="signup-book-call-email-fallback">
        {t('bookCall.emailFallbackLead')}{' '}
        <a
          className="signup-off-path__link"
          href={`mailto:${email}`}
          data-analytics-action="signup.book-call-screen-email.activate"
          data-testid="signup-book-call-email"
        >
          {email}
        </a>{' '}
        {t('bookCall.emailFallbackTail')}
      </p>
      <section aria-label={t('bookCall.calendarLabel')} data-testid="signup-book-call-calendar">
        {calendar ? (
          <BookACallCalendar calendar={calendar} onBooked={onBooked} />
        ) : (
          <p className="signup-off-path__body">{t('bookCall.calendarUnavailable')}</p>
        )}
      </section>
    </div>
  );
}

/** `/book-a-call/`: reached from the error screen and every signup footer. */
export function BookACallScreen({ calendar = CALENDAR }: { calendar?: URL | null }) {
  return (
    <SignupShell width="reading">
      <BookACall calendar={calendar} />
    </SignupShell>
  );
}
